import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/site-content';
import { MapPin, Phone, Mail, Clock, Send, ArrowRight } from 'lucide-react';

const Kontakt = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert('Der opstod en fejl. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Der opstod en fejl. Prøv venligst igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt Lars Nissen Corell – Psykoterapeut MPF | Core Hypnose</title>
        <meta name="description" content="Kontakt Lars Nissen Corell – Psykoterapeut MPF hos Core Hypnose i Valby. Træffes på tlf. 27 20 46 71 eller kontakt@corehypnose.dk. Hurtig svartid og fleksible tider." />
        <link rel="canonical" href="https://corehypnose.dk/kontakt" />
        <meta property="og:title" content="Kontakt Lars Nissen Corell – Psykoterapeut MPF | Core Hypnose" />
        <meta property="og:description" content="Kontakt Lars Nissen Corell – Psykoterapeut MPF hos Core Hypnose i Valby. Træffes på tlf. 27 20 46 71 eller kontakt@corehypnose.dk. Hurtig svartid og fleksible tider." />
        <meta property="og:image" content="" />
        <meta property="og:url" content="https://corehypnose.dk/kontakt" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kontakt Lars Nissen Corell – Psykoterapeut MPF | Core Hypnose" />
        <meta name="twitter:description" content="Kontakt Lars Nissen Corell – Psykoterapeut MPF hos Core Hypnose i Valby. Træffes på tlf. 27 20 46 71 eller kontakt@corehypnose.dk. Hurtig svartid og fleksible tider." />
        <meta name="twitter:image" content="" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 bg-[#F4EFE7]/30 border-b border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-4">Kontakt</span>
              <h1 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6">Lad os tage en snak</h1>
              <p className="text-xl text-[#3B352F]/80 leading-relaxed">
                Har du spørgsmål til metakognitiv terapi, hypnose eller hvordan et forløb foregår? Du er altid velkommen til at kontakte mig. Jeg glæder mig til at høre fra dig.
              </p>
              <p className="text-xl text-[#3B352F]/80 leading-relaxed mt-4">
                Du er også velkommen til at kontakte mig, hvis du er i tvivl om, hvorvidt parterapi eller relationscoaching er det rigtige sted at starte.
              </p>
            </div>
            <div className="lg:col-span-6">
              <div className="relative max-w-md mx-auto lg:max-w-none">
                <div className="absolute inset-0 bg-[#123F13]/5 rounded-2xl transform translate-x-3 translate-y-3 -z-10"></div>
                <img 
                  src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/d9b63796-80d1-4a39-9380-8a525cd1d6aa.png" 
                  alt="Lars Nissen Corell – Psykoterapeut MPF" 
                  className="w-full h-auto object-cover rounded-2xl shadow-lg border border-[#E8DDCE] bg-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Details & Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Contact Info (5 columns) */}
            <div className="lg:col-span-5 space-y-10">
              <div>
                <h2 className="text-2xl font-serif text-[#123F13] mb-4">Praktisk information</h2>
                <p className="text-[#3B352F]/70">
                  Du kan kontakte mig via telefon, e-mail eller ved at udfylde formularen til højre. Jeg bestræber mig på at svare på alle henvendelser inden for 24 timer.
                </p>
              </div>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4EFE7] rounded-lg flex items-center justify-center text-[#123F13] shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#123F13]">Adresse</h3>
                    <p className="text-[#3B352F]/70">Valby Tingsted 7, 1. sal, 2500 Valby</p>
                    <p className="text-sm text-[#3B352F]/50 mt-1">Lige ved Valby Station og busser.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4EFE7] rounded-lg flex items-center justify-center text-[#123F13] shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#123F13]">Telefon</h3>
                    <a href={`tel:${siteConfig.contact.phone}`} className="text-[#3B352F]/70 hover:text-[#123F13] text-lg font-semibold">
                      {siteConfig.contact.phone}
                    </a>
                    <p className="text-sm text-[#3B352F]/50 mt-1">Bedst mellem 9:00 og 16:00.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4EFE7] rounded-lg flex items-center justify-center text-[#123F13] shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#123F13]">E-mail</h3>
                    <a href={`mailto:${siteConfig.contact.email}`} className="text-[#3B352F]/70 hover:text-[#123F13]">
                      {siteConfig.contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4EFE7] rounded-lg flex items-center justify-center text-[#123F13] shrink-0">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#123F13]">Åbningstider</h3>
                    <p className="text-[#3B352F]/70">{siteConfig.contact.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Side (7 columns) */}
            <div className="lg:col-span-7 bg-[#FAF7F2] p-8 md:p-12 rounded-2xl border border-[#E8DDCE]">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#123F13] text-white rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send size={32} />
                  </div>
                  <h3 className="text-2xl font-serif text-[#123F13] mb-4">Tak for din besked</h3>
                  <p className="text-[#3B352F]/70">Jeg vender tilbage til dig så hurtigt som muligt.</p>
                  <button 
                    onClick={() => setIsSubmitted(false)}
                    className="mt-8 text-[#123F13] font-bold underline"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - HIDDEN FROM HUMANS */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Do not fill this if you are human</label>
                    <input type="text" name="website_url" id="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-bold text-[#123F13] uppercase tracking-wider">Navn</label>
                      <input 
                        type="text" 
                        name="name" 
                        id="name" 
                        required 
                        className="w-full px-4 py-3 rounded-md border border-[#E8DDCE] bg-white focus:outline-none focus:ring-2 focus:ring-[#123F13]/20 focus:border-[#123F13] transition-all"
                        placeholder="Dit navn"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-bold text-[#123F13] uppercase tracking-wider">E-mail</label>
                      <input 
                        type="email" 
                        name="email" 
                        id="email" 
                        required 
                        className="w-full px-4 py-3 rounded-md border border-[#E8DDCE] bg-white focus:outline-none focus:ring-2 focus:ring-[#123F13]/20 focus:border-[#123F13] transition-all"
                        placeholder="din@mail.dk"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-bold text-[#123F13] uppercase tracking-wider">Telefon</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      id="phone" 
                      required 
                      className="w-full px-4 py-3 rounded-md border border-[#E8DDCE] bg-white focus:outline-none focus:ring-2 focus:ring-[#123F13]/20 focus:border-[#123F13] transition-all"
                      placeholder="Dit telefonnummer"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-bold text-[#123F13] uppercase tracking-wider">Besked</label>
                    <textarea 
                      name="message" 
                      id="message" 
                      rows="5" 
                      required 
                      className="w-full px-4 py-3 rounded-md border border-[#E8DDCE] bg-white focus:outline-none focus:ring-2 focus:ring-[#123F13]/20 focus:border-[#123F13] transition-all"
                      placeholder="Hvordan kan jeg hjælpe dig?"
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#123F13] text-white py-4 rounded-lg font-bold hover:bg-[#123F13]/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmitting ? 'Sender...' : 'Send besked'}
                    <Send size={18} />
                  </button>
                  <p className="text-xs text-[#3B352F]/50 text-center">
                    Ved at indsende formularen accepterer du, at jeg kontakter dig vedrørende din henvendelse.
                  </p>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[400px] bg-[#E8DDCE] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#E8DDCE] to-[#F4EFE7]">
           <div className="text-center px-6">
             <MapPin size={48} className="text-[#123F13] mx-auto mb-4" />
             <h3 className="text-xl font-serif text-[#123F13] mb-2">Find klinikken i Valby</h3>
             <p className="text-[#3B352F]/70 mb-6">Valby Tingsted 7, 1. sal - Lige ved stationen</p>
             <a 
               href="https://www.google.com/maps/search/Valby+Tingsted+7,+2500+Valby" 
               target="_blank" 
               rel="noopener noreferrer"
               className="inline-block bg-white text-[#123F13] px-6 py-2 rounded-lg font-bold shadow-sm hover:bg-[#FAF7F2] transition-all"
             >
               Åbn i Google Maps
             </a>
           </div>
        </div>
      </section>
    </>
  );
};

export default Kontakt;
